import PostText from './PostText';
import { IconClose } from './talkAppleIcons';
import React, { useCallback, useEffect, useRef, useState } from 'react';

const VIDEO_EXT = /\.(mp4|webm|ogg|mov)(\?|$)/i;

function mediaUrl(url) {
  if (!url) return '';
  if (url.startsWith('http') || url.startsWith('blob:')) return url;
  return url;
}

function isVideoUrl(url) {
  if (!url) return false;
  const lower = url.split('?')[0].toLowerCase();
  return VIDEO_EXT.test(lower) || lower.endsWith('.m4v');
}

function hasSlideContent(post) {
  const t = (post.content || '').trim();
  return !!(post.image_url || post.audio_url || t);
}

export default function StoryViewer({ posts, initialIndex = 0, onClose, title }) {
  const slides = (posts || []).filter(hasSlideContent);
  const [index, setIndex] = useState(() =>
    Math.min(Math.max(0, initialIndex), Math.max(0, slides.length - 1)),
  );
  const [paused, setPaused] = useState(false);
  const [progress, setProgress] = useState(0);
  const timerRef = useRef(null);
  const videoRef = useRef(null);
  const touchStartX = useRef(null);

  const slide = slides[index];
  const durationMs = slide?.audio_url ? 14000 : slide?.image_url && isVideoUrl(slide.image_url) ? 60000 : 6500;

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const goNext = useCallback(() => {
    if (index >= slides.length - 1) {
      onClose?.();
      return;
    }
    setIndex(i => i + 1);
    setProgress(0);
  }, [index, slides.length, onClose]);

  const goPrev = useCallback(() => {
    if (index <= 0) return;
    setIndex(i => i - 1);
    setProgress(0);
  }, [index]);

  useEffect(() => {
    if (!slide || paused) {
      clearTimer();
      return;
    }
    const v = videoRef.current;
    if (slide.image_url && isVideoUrl(slide.image_url) && v) {
      clearTimer();
      const onEnded = () => goNext();
      const onTime = () => {
        const d = v.duration;
        if (d && Number.isFinite(d)) setProgress((v.currentTime / d) * 100);
      };
      v.addEventListener('ended', onEnded);
      v.addEventListener('timeupdate', onTime);
      v.play().catch(() => {});
      return () => {
        v.removeEventListener('ended', onEnded);
        v.removeEventListener('timeupdate', onTime);
        v.pause();
      };
    }

    const tick = 80;
    const step = 100 / (durationMs / tick);
    timerRef.current = setInterval(() => {
      setProgress(p => {
        if (p + step >= 100) {
          clearTimer();
          goNext();
          return 0;
        }
        return p + step;
      });
    }, tick);
    return clearTimer;
  }, [slide, paused, durationMs, goNext, clearTimer]);

  useEffect(() => {
    setProgress(0);
    setPaused(false);
  }, [index]);

  useEffect(() => {
    const onKey = e => {
      if (e.key === 'Escape') onClose?.();
      if (e.key === 'ArrowRight') goNext();
      if (e.key === 'ArrowLeft') goPrev();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, goNext, goPrev]);

  if (!slides.length) {
    return (
      <div style={S.backdrop} role="dialog" aria-modal="true" aria-label="ストーリー">
        <div style={S.emptyWrap}>
          <p style={S.emptyTitle}>表示できるストーリーがありません</p>
          <p style={S.emptyHint}>画像・音声・テキストのある投稿がここに表示されます</p>
          <button data-control="secondary" data-control-id="StoryViewer-closeBtnEmpty" type="button" style={S.closeBtnEmpty} onClick={onClose}>閉じる</button>
        </div>
      </div>
    );
  }

  const onTap = e => {
    const w = window.innerWidth;
    const x = e.clientX ?? e.changedTouches?.[0]?.clientX ?? w / 2;
    if (x < w * 0.28) goPrev();
    else if (x > w * 0.72) goNext();
    else setPaused(p => !p);
  };

  const onTouchStart = e => {
    touchStartX.current = e.changedTouches[0]?.clientX ?? null;
  };
  const onTouchEnd = e => {
    const start = touchStartX.current;
    if (start == null) return;
    const end = e.changedTouches[0]?.clientX ?? start;
    const dx = end - start;
    if (dx > 50) goPrev();
    else if (dx < -50) goNext();
    touchStartX.current = null;
  };

  const imgUrl = slide?.image_url ? mediaUrl(slide.image_url) : '';
  const audioUrl = slide?.audio_url ? mediaUrl(slide.audio_url) : '';
  const text = (slide?.content || '').trim();
  const isVideo = imgUrl && isVideoUrl(imgUrl);

  return (
    <div
      data-control-theme="dark" style={S.backdrop}
      role="dialog"
      aria-modal="true"
      aria-label="ストーリー"
      onClick={onTap}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      <div style={S.topBar} onClick={e => { e.stopPropagation(); }}>
        <div style={S.progressRow}>
          {slides.map((_, i) => (
            <div key={i} style={S.progressTrack}>
              <div
                style={{
                  ...S.progressFill,
                  width: `${i < index ? 100 : i === index ? progress : 0}%`,
                  transition: i === index ? 'none' : 'width 0.2s',
                }}
              />
            </div>
          ))}
        </div>
        <div style={S.headerRow}>
          <span style={S.headerTitle}>{title || 'ストーリー'}</span>
          <button data-control="icon" data-control-id="StoryViewer-closeBtn"
            type="button"
            style={S.closeBtn}
            onClick={e => { e.stopPropagation(); onClose?.(); }}
            aria-label="閉じる"
          >
            <IconClose />
          </button>
        </div>
      </div>

      <div style={S.slideArea}>
        {slide && (
          <>
            <div style={S.authorRow}>
              <div style={S.authorAvatar}>
                {slide.avatar
                  ? <img src={slide.avatar} alt="" style={S.authorAvatarImg} />
                  : <span style={S.authorAvatarTxt}>{(slide.username || '?')[0].toUpperCase()}</span>}
              </div>
              <span style={S.authorName}>{slide.username}</span>
            </div>

            <div style={S.mediaWrap}>
              {isVideo && (
                <video
                  ref={videoRef}
                  key={slide.id + imgUrl}
                  src={imgUrl}
                  style={S.video}
                  playsInline
                  controls
                  onClick={e => e.stopPropagation()}
                />
              )}
              {!isVideo && imgUrl && (
                <img src={imgUrl} alt="" style={S.image} />
              )}
              {audioUrl && (
                <div style={S.audioBox} onClick={e => e.stopPropagation()}>
                  <audio
                    key={slide.id + audioUrl}
                    src={audioUrl}
                    controls
                    style={S.audio}
                  />
                </div>
              )}
            </div>

            {!!text && (
              <div style={S.caption}>
                <p style={S.captionText}><PostText content={text} onTagClick={onClose} /></p>
              </div>
            )}

            {paused && (
              <div style={S.pauseOverlay} aria-hidden>
                <span style={S.pauseIcon}>❚❚</span>
              </div>
            )}
          </>
        )}
      </div>

      <div style={S.hint} aria-hidden>左右タップで送り · 中央で一時停止</div>
    </div>
  );
}

const S = {
  backdrop: {
    position: 'fixed',
    inset: 0,
    zIndex: 200,
    background: '#0a0a0a',
    display: 'flex',
    flexDirection: 'column',
    maxWidth: 'var(--app-max-w)',
    margin: '0 auto',
    left: 0,
    right: 0,
    touchAction: 'manipulation',
  },
  topBar: {
    padding: '10px 12px 0',
    flexShrink: 0,
  },
  progressRow: {
    display: 'flex',
    gap: 4,
    marginBottom: 10,
  },
  progressTrack: {
    flex: 1,
    height: 3,
    borderRadius: 2,
    background: 'rgba(255,255,255,0.25)',
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
    background: '#fff',
    borderRadius: 2,
  },
  headerRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 6,
  },
  headerTitle: {
    fontSize: 13,
    fontWeight: 700,
    color: 'rgba(255,255,255,0.85)',
    letterSpacing: '0.04em',
  },
  closeBtn: {
    width: 36,
    height: 36,
    borderRadius: '50%',
    border: 'none',
    background: 'rgba(255,255,255,0.12)',
    color: '#fff',
    fontSize: 18,
    cursor: 'pointer',
    lineHeight: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  slideArea: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
    padding: '0 12px 12px',
    position: 'relative',
  },
  authorRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    marginBottom: 10,
  },
  authorAvatar: {
    width: 36,
    height: 36,
    borderRadius: '50%',
    background: 'linear-gradient(45deg,#f09433,#bc1888)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
  },
  authorAvatarImg: { width: '100%', height: '100%', objectFit: 'cover' },
  authorAvatarTxt: { color: '#fff', fontWeight: 700, fontSize: 15 },
  authorName: {
    fontSize: 15,
    fontWeight: 700,
    color: '#fff',
  },
  mediaWrap: {
    flex: 1,
    minHeight: 200,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 12,
    overflow: 'hidden',
    background: 'rgba(255,255,255,0.06)',
  },
  image: {
    maxWidth: '100%',
    maxHeight: '52vh',
    objectFit: 'contain',
  },
  video: {
    width: '100%',
    maxHeight: '52vh',
    objectFit: 'contain',
    background: '#000',
  },
  audioBox: {
    width: '100%',
    padding: '16px 12px',
  },
  audio: { width: '100%', height: 40 },
  caption: {
    marginTop: 12,
    padding: '10px 12px',
    borderRadius: 10,
    background: 'rgba(0,0,0,0.45)',
    maxHeight: '22vh',
    overflow: 'auto',
    WebkitOverflowScrolling: 'touch',
    overscrollBehavior: 'contain',
  },
  captionText: {
    margin: 0,
    fontSize: 16,
    lineHeight: 1.55,
    color: '#fff',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  },
  pauseOverlay: {
    position: 'absolute',
    inset: 0,
    top: 80,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    pointerEvents: 'none',
  },
  pauseIcon: {
    fontSize: 42,
    color: 'rgba(255,255,255,0.5)',
    letterSpacing: 4,
  },
  hint: {
    textAlign: 'center',
    fontSize: 11,
    color: 'rgba(255,255,255,0.35)',
    padding: '6px 12px 14px',
  },
  emptyWrap: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 24,
    gap: 12,
  },
  emptyTitle: {
    color: '#fff',
    fontSize: 16,
    fontWeight: 700,
    margin: 0,
    textAlign: 'center',
  },
  emptyHint: {
    color: 'rgba(255,255,255,0.55)',
    fontSize: 13,
    margin: 0,
    textAlign: 'center',
    lineHeight: 1.6,
  },
  closeBtnEmpty: {
    marginTop: 16,
    padding: '10px 28px',
    borderRadius: 9999,
    border: 'none',
    background: 'rgba(255,255,255,0.2)',
    color: '#fff',
    fontWeight: 700,
    fontSize: 14,
    cursor: 'pointer',
    fontFamily: 'inherit',
  },
};
